import React, { useEffect, useState } from 'react';
import { getTestRunStatus } from '../../services/api';
import './AdminTestStyles.css';

function TestProgressModal({ testRunId, onComplete, onClose }) {
  const [progressData, setProgressData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!testRunId) return;

    // Poll for status updates every 500ms
    const pollInterval = setInterval(async () => {
      try {
        const status = await getTestRunStatus(testRunId);
        setProgressData(status);

        // Stop polling if tests are complete
        if (status.status !== 'running' && status.status !== 'pending') {
          clearInterval(pollInterval);
          // Auto-close modal after 3 seconds when complete
          setTimeout(() => {
            onComplete && onComplete(status);
          }, 3000);
        }
      } catch (err) {
        console.error('Failed to fetch test progress:', err);
        setError('Failed to fetch progress updates');
        clearInterval(pollInterval);
      }
    }, 500);

    return () => clearInterval(pollInterval);
  }, [testRunId]); // onComplete is intentionally excluded to prevent infinite loop

  if (!progressData) {
    return (
      <div className="test-progress-modal-overlay">
        <div className="test-progress-modal">
          <div className="progress-header">
            <h2>Preparing Tests...</h2>
          </div>
          <div className="progress-content">
            <div className="spinner"></div>
            <p>Initializing test runner...</p>
          </div>
        </div>
      </div>
    );
  }

  const { status, total_tests, passed_tests, failed_tests, skipped_tests, current_test } = progressData;
  // Skipped tests count toward completion (production skips many by policy)
  const skippedTests = skipped_tests || 0;
  const completedTests = (passed_tests || 0) + (failed_tests || 0) + skippedTests;
  const progressPercent = total_tests > 0 ? Math.min(100, (completedTests / total_tests) * 100) : 0;

  const isComplete = status === 'completed' || status === 'failed';

  return (
    <div className="test-progress-modal-overlay">
      <div className="test-progress-modal">
        <div className="progress-header">
          <h2>{isComplete ? 'Tests Complete!' : 'Running Tests...'}</h2>
          {!isComplete && (
            <button className="close-btn" onClick={onClose} title="Stop watching (tests continue in background)">
              ×
            </button>
          )}
        </div>

        <div className="progress-content">
          {error && <div className="progress-error">{error}</div>}

          {/* Progress Counter */}
          <div className="progress-counter">
            <span className="progress-text">
              {isComplete ? (
                `Completed ${total_tests} tests`
              ) : (
                `Running test ${Math.min(completedTests + 1, total_tests)} of ${total_tests}`
              )}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="progress-percentage">{Math.round(progressPercent)}%</div>

          {/* Current Test Info */}
          {current_test && !isComplete && (
            <div className="current-test-info">
              <div className="current-test-label">Current Test:</div>
              <div className="current-test-name">{current_test.display}</div>
            </div>
          )}

          {/* Results Summary */}
          <div className="progress-summary">
            <div className="summary-item passed">
              <span className="summary-label">Passed:</span>
              <span className="summary-value">{passed_tests}</span>
            </div>
            <div className="summary-item failed">
              <span className="summary-label">Failed:</span>
              <span className="summary-value">{failed_tests}</span>
            </div>
            <div className="summary-item skipped">
              <span className="summary-label">Skipped:</span>
              <span className="summary-value skipped-count">{skippedTests}</span>
            </div>
            <div className="summary-item total">
              <span className="summary-label">Total:</span>
              <span className="summary-value">{total_tests}</span>
            </div>
          </div>

          {/* Completion Message */}
          {isComplete && (
            <div className="completion-message">
              <p>
                {status === 'completed'
                  ? '✓ All tests completed successfully!'
                  : '✗ Test run failed'}
              </p>
              {skippedTests > 0 && (
                <p>{skippedTests} skipped (the reasons are in the results)</p>
              )}
              <p className="auto-close-hint">Closing in 3 seconds...</p>
            </div>
          )}

          {/* Status Indicator */}
          {!isComplete && (
            <div className="status-indicator">
              <div className="spinner-small"></div>
              <span>Tests running in background...</span>
            </div>
          )}
        </div>

        {/* Close Button */}
        {isComplete && (
          <div className="progress-actions">
            <button className="close-complete-btn" onClick={() => onComplete && onComplete(progressData)}>
              View Results
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TestProgressModal;
